import type { GeoLocation } from '@/services/timeAttendance/types';

export interface GeolocationCaptureResult {
  success: boolean;
  location?: GeoLocation;
  error?: string;
  errorCode?: 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'TIMEOUT' | 'UNSUPPORTED';
  /** Duration in milliseconds for the capture attempt */
  durationMs?: number;
  /** Whether a retry was attempted */
  wasRetry?: boolean;
}

interface CaptureOptions {
  enableHighAccuracy: boolean;
  timeout: number;
  maximumAge: number;
}

/**
 * Attempts to get the current position with the given options.
 * Returns a promise that resolves with the result (success or failure).
 */
const attemptCapture = (
  options: CaptureOptions,
  startTime: number
): Promise<GeolocationCaptureResult> => {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const durationMs = Date.now() - startTime;
        console.log(`[Geolocation] ✓ Success in ${durationMs}ms`, {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          highAccuracy: options.enableHighAccuracy,
        });
        
        resolve({
          success: true,
          location: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy_meters: position.coords.accuracy,
            captured_at: new Date().toISOString(),
          },
          durationMs,
        });
      },
      (error) => {
        const durationMs = Date.now() - startTime;
        let errorCode: GeolocationCaptureResult['errorCode'] = 'POSITION_UNAVAILABLE';
        let errorMessage = 'Unable to retrieve location';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorCode = 'PERMISSION_DENIED';
            errorMessage = 'Location permission denied';
            break;
          case error.POSITION_UNAVAILABLE:
            errorCode = 'POSITION_UNAVAILABLE';
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorCode = 'TIMEOUT';
            errorMessage = 'Location request timed out';
            break;
        }

        console.warn(`[Geolocation] ✗ Failed in ${durationMs}ms:`, {
          errorCode,
          errorMessage,
          highAccuracy: options.enableHighAccuracy,
          timeout: options.timeout,
        });

        resolve({
          success: false,
          error: errorMessage,
          errorCode,
          durationMs,
        });
      },
      options
    );
  });
};

/**
 * Captures the user's current geolocation with retry logic.
 * 
 * Strategy:
 * 1. First attempt: High accuracy with 10s timeout
 * 2. If TIMEOUT: Retry with low accuracy (faster, uses cell/wifi)
 * 3. Returns the result with timing metrics for debugging
 */
export const captureGeolocation = async (): Promise<GeolocationCaptureResult> => {
  const overallStart = Date.now();
  
  console.log('[Geolocation] Starting capture...');

  if (!navigator.geolocation) {
    console.error('[Geolocation] Browser does not support geolocation');
    return {
      success: false,
      error: 'Geolocation is not supported by this browser',
      errorCode: 'UNSUPPORTED',
      durationMs: 0,
    };
  }

  // First attempt: High accuracy with 10s timeout
  const highAccuracyOptions: CaptureOptions = {
    enableHighAccuracy: true,
    timeout: 10000, // 10 seconds
    maximumAge: 0,
  };

  const firstAttempt = await attemptCapture(highAccuracyOptions, overallStart);

  if (firstAttempt.success) {
    return firstAttempt;
  }

  // Only retry on TIMEOUT - other errors (permission, unavailable) won't improve with retry
  if (firstAttempt.errorCode === 'TIMEOUT') {
    console.log('[Geolocation] High accuracy timed out, retrying with low accuracy...');

    // Second attempt: Low accuracy (faster, uses network-based location)
    const lowAccuracyOptions: CaptureOptions = {
      enableHighAccuracy: false,
      timeout: 8000, // 8 seconds for fallback
      maximumAge: 60000, // Accept cached position up to 1 minute old
    };

    const retryStart = Date.now();
    const retryAttempt = await attemptCapture(lowAccuracyOptions, retryStart);

    if (retryAttempt.success) {
      return {
        ...retryAttempt,
        wasRetry: true,
        durationMs: Date.now() - overallStart,
      };
    }

    // Both attempts failed
    console.error('[Geolocation] Both attempts failed', {
      totalDurationMs: Date.now() - overallStart,
    });

    return {
      ...retryAttempt,
      wasRetry: true,
      durationMs: Date.now() - overallStart,
    };
  }

  // Non-timeout error - return first attempt result
  return firstAttempt;
};

export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return Math.round(R * c); // Distance in meters
};

export const formatDistance = (meters: number): string => {
  if (meters < 1000) {
    return `${meters}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
};
